/**
 * Notification Provider Component
 * 
 * Renders toast notifications. Add this to your app root.
 * 
 * @example
 * ```tsx
 * // In App.tsx
 * import { NotificationProvider } from '@/shared/components/ui';
 * 
 * function App() {
 *   return (
 *     <>
 *       <RouterProvider router={router} />
 *       <NotificationProvider />
 *     </>
 *   );
 * }
 * ```
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import {
  Notification,
  onNotification,
  onDismiss,
  onClear,
  notify,
} from '../../services/notification.service';

// =============================================================================
// PAUSE-ON-HOVER TIMER HOOK
// =============================================================================

/**
 * All timer logic lives in refs — no useEffect re-runs on hover/remaining changes.
 * Returns bar animation props and mouse handlers.
 */
function usePauseOnHoverTimer(duration: number | undefined, onDismiss: () => void) {
  // ref to always have the latest onDismiss without stale closures
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingMsRef = useRef<number>(duration ?? 0);
  const startedAtRef = useRef<number | null>(null);

  // Progress bar: scale (1→0) + how long the animation takes
  const [bar, setBar] = useState({ scale: 1, transitionDuration: (duration ?? 0) / 1000 });

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(
    (ms: number) => {
      clearTimer();
      if (ms <= 0) {
        dismissRef.current();
        return;
      }
      remainingMsRef.current = ms;
      startedAtRef.current = Date.now();
      timerRef.current = setTimeout(() => dismissRef.current(), ms);
      // Animate bar from current scale to 0 over the remaining time
      setBar({ scale: 0, transitionDuration: ms / 1000 });
    },
    [clearTimer],
  );

  // Start timer once on mount
  useEffect(() => {
    if (!duration || duration <= 0) return;
    remainingMsRef.current = duration;
    startTimer(duration);
    return clearTimer;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount to start dismiss timer
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!duration || duration <= 0) return;
    // Calculate remaining time and pause
    if (startedAtRef.current != null) {
      const elapsed = Date.now() - startedAtRef.current;
      remainingMsRef.current = Math.max(0, remainingMsRef.current - elapsed);
      startedAtRef.current = null;
    }
    clearTimer();
    // Freeze bar at current position (instant transition)
    setBar({
      scale: duration > 0 ? remainingMsRef.current / duration : 0,
      transitionDuration: 0.05,
    });
  }, [clearTimer, duration]);

  const handleMouseLeave = useCallback(() => {
    if (!duration || duration <= 0) return;
    startTimer(remainingMsRef.current);
  }, [startTimer, duration]);

  return { bar, handleMouseEnter, handleMouseLeave };
}

// =============================================================================
// NOTIFICATION ITEM
// =============================================================================

interface NotificationItemProps {
  notification: Notification;
  onDismiss: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onDismiss,
}) => {
  const { type, title, message, duration } = notification;
  const { bar, handleMouseEnter, handleMouseLeave } = usePauseOnHoverTimer(duration, onDismiss);

  const config = {
    success: {
      icon: CheckCircle,
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      iconColor: 'text-emerald-500',
      titleColor: 'text-emerald-800',
      textColor: 'text-emerald-700',
    },
    error: {
      icon: XCircle,
      bg: 'bg-red-50',
      border: 'border-red-200',
      iconColor: 'text-[#c00035]',
      titleColor: 'text-red-800',
      textColor: 'text-[#c00035]',
    },
    warning: {
      icon: AlertTriangle,
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      iconColor: 'text-amber-500',
      titleColor: 'text-amber-800',
      textColor: 'text-amber-700',
    },
    info: {
      icon: Info,
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      iconColor: 'text-blue-500',
      titleColor: 'text-blue-800',
      textColor: 'text-blue-700',
    },
  };

  const { icon: Icon, bg, border, iconColor, titleColor, textColor } = config[type];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`
        relative flex items-start gap-3 p-4 pr-10
        ${bg} ${border} border
        rounded-xl shadow-lg backdrop-blur-sm
        max-w-md w-full
      `}
    >
      <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />

      <div className="flex-1 min-w-0">
        {title && (
          <p className={`font-semibold text-sm ${titleColor}`}>{title}</p>
        )}
        <p className={`text-sm capitalize ${textColor} ${title ? 'mt-0.5' : ''}`}>
          {message}
        </p>

        {notification.action && (
          <button
            onClick={() => {
              notification.action?.onClick();
              onDismiss();
            }}
            className={`mt-2 text-sm font-medium ${iconColor} hover:underline`}
          >
            {notification.action.label}
          </button>
        )}
      </div>

      <button
        onClick={onDismiss}
        className="absolute top-3 right-3 p-1 rounded-lg hover:bg-black/5 transition-colors"
      >
        <X className="w-4 h-4 text-gray-400" />
      </button>

      {duration != null && duration > 0 && (
        <motion.div
          initial={{ scaleX: 1 }}
          animate={{ scaleX: bar.scale }}
          transition={{ duration: bar.transitionDuration, ease: 'linear' }}
          className={`absolute bottom-0 left-0 right-0 h-1 ${iconColor.replace('text-', 'bg-')} opacity-30 origin-left rounded-b-xl`}
        />
      )}
    </motion.div>
  );
};

// =============================================================================
// REALTIME NOTIFICATION ITEM
// =============================================================================

const RealtimeNotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onDismiss,
}) => {
  const { message, duration, appName, appLogoUrl, onClickNavigate } = notification;
  const { bar, handleMouseEnter, handleMouseLeave } = usePauseOnHoverTimer(duration, onDismiss);

  const isClickable = !!onClickNavigate;

  const handleClick = () => {
    if (!isClickable) return;
    onClickNavigate();
    onDismiss();
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 80 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 80 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative min-w-[360px] max-w-md w-full bg-white border border-gray-200 border-l-[3px] border-l-[#c00035] rounded-xl shadow-xl overflow-hidden transition-shadow ${isClickable ? 'cursor-pointer hover:shadow-2xl' : 'cursor-default'}`}
    >
      <div className="p-4 pr-10">
        <div className="flex items-center gap-2.5 mb-2">
          {appLogoUrl && (
            <img
              src={appLogoUrl}
              alt=""
              className="w-8 h-8 object-contain flex-shrink-0"
            />
          )}
          {appName && (
            <span className="font-semibold text-sm text-gray-900">{appName}</span>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span className="text-xs font-medium text-emerald-600">Live</span>
          </div>
        </div>

        <p className="text-sm text-gray-700">{message}</p>
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        className="absolute top-3 right-3 p-1 rounded-lg hover:bg-black/5 transition-colors"
      >
        <X className="w-4 h-4 text-gray-400" />
      </button>

      {duration != null && duration > 0 && (
        <motion.div
          initial={{ scaleX: 1 }}
          animate={{ scaleX: bar.scale }}
          transition={{ duration: bar.transitionDuration, ease: 'linear' }}
          className="absolute bottom-0 left-0 right-0 h-1 bg-[#c00035] opacity-25 origin-left"
        />
      )}
    </motion.div>
  );
};

// =============================================================================
// NOTIFICATION PROVIDER
// =============================================================================

export const NotificationProvider: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    const unsubNotification = onNotification((notification) => {
      setNotifications((prev) => [...prev, notification]);
    });

    const unsubDismiss = onDismiss((id) => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    });

    const unsubClear = onClear(() => {
      setNotifications([]);
    });

    return () => {
      unsubNotification();
      unsubDismiss();
      unsubClear();
    };
  }, []);

  const handleDismiss = useCallback((id: string) => {
    notify.dismiss(id);
  }, []);

  if (notifications.length === 0) return null;

  return createPortal(
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3">
      <AnimatePresence mode="sync">
        {notifications.map((notification) =>
          notification.variant === 'realtime' ? (
            <RealtimeNotificationItem
              key={notification.id}
              notification={notification}
              onDismiss={() => handleDismiss(notification.id)}
            />
          ) : (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onDismiss={() => handleDismiss(notification.id)}
            />
          )
        )}
      </AnimatePresence>
    </div>,
    document.body
  );
};

export default NotificationProvider;
